import api from './api';
import IIdResult from './interfaces/results/IIdResult';

// The end of the offer PDF of an offer ('offers') or an additional offer ('additionaloffers')
export type OfferOwner = 'offers' | 'additionaloffers';

export type ExtraPageType = 'image' | 'pdf' | 'text';

// A page of its own after the delivery terms: a text, an image or an attached PDF
export interface IOfferExtraPage {
  fileId: null | string;
  fileName: null | string;
  id: number;
  mimeType: null | string;
  orderNumber: number;
  text: null | string;
  title: null | string;
  type: ExtraPageType;
}

export interface IOfferExtraPageInput {
  fileId: null | string;
  text: null | string;
  title: string;
  type: ExtraPageType;
}

// Whether the offer text and the information with the signature start on a new page, and
// which prices the PDF shows
export interface IOfferPdfSettings {
  hasOfferText: boolean;
  offerTextPageBreakBefore: boolean;
  // The total of the whole offer on a row of its own after the positions
  showOfferTotal: boolean;
  // The price of each position (shown unless chosen otherwise)
  showPositionPrices: boolean;
  // The total of each work type on the row of its heading
  showWorkTypeTotals: boolean;
  signaturePageBreakBefore: boolean;
}

export default {
  pdfSettings: {
    get: (owner: OfferOwner, id: number): Promise<IOfferPdfSettings> =>
      api.get(`/api/v1/${owner}/${id}/pdf-settings`),
    patch: (
      owner: OfferOwner,
      id: number,
      // The settings not given keep their values
      settings: Partial<Omit<IOfferPdfSettings, 'hasOfferText'>>
    ): Promise<IIdResult> =>
      api.patch(`/api/v1/${owner}/${id}/pdf-settings`, settings),
  },
  extraPages: {
    delete: (
      owner: OfferOwner,
      id: number,
      pageId: number
    ): Promise<IIdResult> =>
      api.delete(`/api/v1/${owner}/${id}/extra-pages/${pageId}`),
    get: (owner: OfferOwner, id: number): Promise<IOfferExtraPage[]> =>
      api.get(`/api/v1/${owner}/${id}/extra-pages`),
    getFile: (owner: OfferOwner, id: number, pageId: number): Promise<any> =>
      api.getFile(`/api/v1/${owner}/${id}/extra-pages/${pageId}/file`),
    // The ids of all the pages in their new order
    putOrder: (
      owner: OfferOwner,
      id: number,
      pageIds: number[]
    ): Promise<IIdResult> =>
      api.put(`/api/v1/${owner}/${id}/extra-pages-order`, pageIds),
    // A new page without a page id
    save: (
      owner: OfferOwner,
      id: number,
      pageId: null | number,
      page: IOfferExtraPageInput
    ): Promise<IIdResult> =>
      pageId === null
        ? api.post(`/api/v1/${owner}/${id}/extra-pages`, page)
        : api.put(`/api/v1/${owner}/${id}/extra-pages/${pageId}`, page),
  },
};
